import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import type { Drill, DrillShotType } from '../types/lesson';
import { getAllDrillsForCoach } from '../services/drills';
import { AddDrillModal } from './AddDrillModal';
import VideoFrameThumb from './VideoFrameThumb';

function isPlaceholderUrl(url: string | undefined): boolean {
  return !url || url.startsWith('https://placeholder.drill/');
}

interface DrillBrowserProps {
  coachId: string;
  /** IDs of drills already attached to this lesson */
  selectedDrillIds: string[];
  /** Called when coach toggles a drill on/off */
  onToggleDrill: (drill: Drill) => void;
}

const SHOT_TYPE_LABELS: Record<DrillShotType, string> = {
  forehand: 'Forehand',
  backhand: 'Backhand',
  serve: 'Serve',
  volley: 'Volley',
  return: 'Return',
  movement: 'Movement',
  general: 'General',
};

const SHOT_TYPE_ORDER: DrillShotType[] = [
  'forehand', 'backhand', 'serve', 'volley', 'return', 'movement', 'general',
];

export function DrillBrowser({
  coachId,
  selectedDrillIds,
  onToggleDrill,
}: DrillBrowserProps) {
  const [drills, setDrills] = useState<Drill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [activeFilter, setActiveFilter] = useState<DrillShotType | 'all'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [infoModalDrill, setInfoModalDrill] = useState<Drill | null>(null);
  const [previewDrill, setPreviewDrill] = useState<Drill | null>(null);
  const [showAddDrill, setShowAddDrill] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(false);
    getAllDrillsForCoach(coachId)
      .then((results) => {
        if (!cancelled) {
          setDrills(results);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setDrills([]);
          setLoading(false);
          setError(true);
        }
      });
    return () => { cancelled = true; };
  }, [coachId]);

  const reloadDrills = () => {
    setLoading(true);
    setError(false);
    getAllDrillsForCoach(coachId)
      .then((results) => {
        setDrills(results);
        setLoading(false);
      })
      .catch(() => {
        setDrills([]);
        setLoading(false);
        setError(true);
      });
  };

  const filtered = useMemo(() => {
    let list = drills;
    if (activeFilter !== 'all') {
      list = list.filter((d) => d.shotType === activeFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(
        (d) =>
          d.title.toLowerCase().includes(q) ||
          d.description.toLowerCase().includes(q)
      );
    }
    return list;
  }, [drills, activeFilter, searchQuery]);

  const selectedSet = useMemo(() => new Set(selectedDrillIds), [selectedDrillIds]);

  return (
    <div className="flex flex-col gap-3">
      {/* 1. Search bar */}
      <div className="relative">
        <svg
          className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#5C6B75]"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <circle cx="11" cy="11" r="8" />
          <line x1="21" y1="21" x2="16.65" y2="16.65" />
        </svg>
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Search drills…"
          className="w-full bg-[#1A2B42] border border-[#2A3F5C] rounded-lg pl-8 pr-3 py-1.5 text-[16px] leading-[21px] text-[#F1F5F9] placeholder-[#5C6B75] focus:outline-none focus:border-[#1A6B8A]"
        />
      </div>

      {/* 2. Shot type filter chips */}
      <div className="flex gap-1.5 overflow-x-auto pb-1" style={{ scrollbarWidth: 'thin' }}>
        <button
          type="button"
          onClick={() => setActiveFilter('all')}
          className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
            activeFilter === 'all'
              ? 'bg-[#1A6B8A] text-white'
              : 'bg-[#1A2B42] text-[#8899AA] hover:text-[#F1F5F9]'
          }`}
        >
          All
        </button>
        {SHOT_TYPE_ORDER.map((shotType) => (
          <button
            key={shotType}
            type="button"
            onClick={() => setActiveFilter(shotType)}
            className={`shrink-0 px-3 py-1 rounded-full text-xs font-medium transition-colors ${
              activeFilter === shotType
                ? 'bg-[#1A6B8A] text-white'
                : 'bg-[#1A2B42] text-[#8899AA] hover:text-[#F1F5F9]'
            }`}
          >
            {SHOT_TYPE_LABELS[shotType]}
          </button>
        ))}
      </div>

      {/* 3. Drill grid */}
      {loading ? (
        <div className="grid grid-cols-2 gap-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex flex-col gap-1.5">
              <div className="aspect-video bg-[#1A2B42] animate-pulse rounded-lg" />
              <div className="h-3 bg-[#1A2B42] animate-pulse rounded w-3/4" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#8899AA" strokeWidth="2" className="mb-2">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <div className="text-sm font-medium text-[#F1F5F9] mb-1">Couldn't load drills</div>
          <div className="text-xs text-[#5C6B75] mb-3">Check your connection and try again</div>
          <button
            type="button"
            onClick={reloadDrills}
            className="px-4 py-1.5 bg-[#1A6B8A] text-white text-xs font-medium rounded-lg hover:bg-[#0F4A63] transition-colors"
          >
            Retry
          </button>
        </div>
      ) : filtered.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-10 text-center">
          <div className="text-sm font-medium text-[#F1F5F9] mb-1">No drills found</div>
          <div className="text-xs text-[#5C6B75]">
            {searchQuery.trim() ? 'Try a different search' : 'No drills for this shot type'}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-2">
          {filtered.map((drill) => {
            const isSelected = selectedSet.has(drill.id);
            return (
              <div
                key={drill.id}
                data-testid="drill-browser-item"
                className={`flex flex-col gap-1.5 rounded-lg p-1 btn-press ${
                  isSelected ? 'ring-2 ring-[#1A6B8A]' : ''
                }`}
              >
                <button
                  type="button"
                  onClick={() => setPreviewDrill(drill)}
                  className="aspect-video bg-[#0F1A2E] rounded-lg overflow-hidden relative flex items-center justify-center group"
                >
                  {drill.thumbnailUrl ? (
                    <img
                      src={drill.thumbnailUrl}
                      alt={drill.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <VideoFrameThumb
                      videoUrl={isPlaceholderUrl(drill.videoUrl) ? null : drill.videoUrl}
                      alt={drill.title}
                      className="w-full h-full object-cover"
                      placeholder={
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="#5C6B75">
                          <polygon points="6 4 20 12 6 20 6 4" />
                        </svg>
                      }
                    />
                  )}
                  {/* Hover play overlay */}
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full bg-white/0 group-hover:bg-white/90 transition-all flex items-center justify-center">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="#0F1A2E"
                        className="opacity-0 group-hover:opacity-100 transition-opacity ml-0.5"
                      >
                        <polygon points="6 4 20 12 6 20 6 4" />
                      </svg>
                    </div>
                  </div>
                </button>
                <div className="flex items-center gap-1 px-0.5">
                  <div className="text-xs font-medium text-[#F1F5F9] truncate flex-1">
                    {drill.title}
                  </div>
                  <button
                    type="button"
                    onClick={() => setInfoModalDrill(drill)}
                    className="w-6 h-6 rounded-full flex items-center justify-center text-[#8899AA] hover:text-[#F1F5F9] hover:bg-[#1A2B42] transition-colors shrink-0"
                    aria-label={`Info about ${drill.title}`}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="16" x2="12" y2="12" />
                      <line x1="12" y1="8" x2="12.01" y2="8" />
                    </svg>
                  </button>
                  <button
                    type="button"
                    data-testid="drill-toggle-button"
                    onClick={() => onToggleDrill(drill)}
                    className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 transition-colors ${
                      isSelected
                        ? 'bg-[#1A6B8A] text-white'
                        : 'bg-[#1A2B42] text-[#8899AA] hover:text-[#F1F5F9]'
                    }`}
                    aria-label={isSelected ? `Remove ${drill.title}` : `Add ${drill.title}`}
                  >
                    {isSelected ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <line x1="12" y1="5" x2="12" y2="19" />
                        <line x1="5" y1="12" x2="19" y2="12" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* 4. Upload Custom Drill */}
      <button
        type="button"
        onClick={() => setShowAddDrill(true)}
        className="w-full flex items-center justify-center gap-1.5 bg-[#1A2B42] text-[#8899AA] hover:text-[#F1F5F9] border border-dashed border-[#2A3F5C] rounded-lg py-2 text-xs transition-colors"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
          <polyline points="17 8 12 3 7 8" />
          <line x1="12" y1="3" x2="12" y2="15" />
        </svg>
        Upload Custom Drill
      </button>

      {/* Add drill modal */}
      <AddDrillModal
        isOpen={showAddDrill}
        onClose={() => setShowAddDrill(false)}
        coachId={coachId}
        onDrillCreated={(drillId) => {
          setShowAddDrill(false);
          getAllDrillsForCoach(coachId).then((updated) => {
            setDrills(updated);
            const newDrill = updated.find((d) => d.id === drillId);
            if (newDrill) onToggleDrill(newDrill);
          });
        }}
      />

      {/* 5. Info modal */}
      <AnimatePresence>
        {infoModalDrill && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setInfoModalDrill(null)}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#152238] border border-[#2A3F5C] rounded-xl p-5 max-w-md w-full shadow-2xl"
            >
              <div className="flex items-start justify-between gap-3 mb-2">
                <h2 className="text-base font-bold text-[#F1F5F9]">{infoModalDrill.title}</h2>
                <button
                  type="button"
                  onClick={() => setInfoModalDrill(null)}
                  className="w-7 h-7 rounded-full flex items-center justify-center text-[#8899AA] hover:text-[#F1F5F9] hover:bg-[#1A2B42] shrink-0"
                  aria-label="Close"
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="inline-block px-2 py-0.5 rounded-full bg-[#1A2B42] text-[#8899AA] text-[10px] font-medium uppercase tracking-wider mb-3">
                {SHOT_TYPE_LABELS[infoModalDrill.shotType]}
              </div>
              <p className="text-sm text-[#D1D9E0] leading-relaxed whitespace-pre-line mb-5">
                {infoModalDrill.description}
              </p>
              <button
                type="button"
                onClick={() => setInfoModalDrill(null)}
                className="w-full bg-[#1A6B8A] text-white text-sm font-semibold rounded-lg py-2 btn-press"
              >
                Got it
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* 6. Video preview modal */}
      <AnimatePresence>
        {previewDrill && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setPreviewDrill(null)}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#152238] border border-[#2A3F5C] rounded-xl overflow-hidden max-w-lg w-full shadow-2xl"
            >
              {/* Video area */}
              <div className="aspect-video bg-black relative">
                {previewDrill.videoUrl && !previewDrill.videoUrl.startsWith('https://placeholder') ? (
                  <video
                    src={previewDrill.videoUrl}
                    controls
                    autoPlay
                    playsInline
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="w-full h-full flex flex-col items-center justify-center text-[#5C6B75]">
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="currentColor">
                      <polygon points="6 4 20 12 6 20 6 4" />
                    </svg>
                    <span className="text-xs mt-2">Video preview not available</span>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="p-4">
                <div className="flex items-start justify-between gap-3 mb-1">
                  <h3 className="text-sm font-bold text-[#F1F5F9]">{previewDrill.title}</h3>
                  <span className="shrink-0 px-2 py-0.5 rounded-full bg-[#1A2B42] text-[#8899AA] text-[10px] font-medium uppercase tracking-wider">
                    {SHOT_TYPE_LABELS[previewDrill.shotType]}
                  </span>
                </div>
                {previewDrill.description && (
                  <p className="text-xs text-[#8899AA] leading-relaxed line-clamp-2 mb-3">
                    {previewDrill.description}
                  </p>
                )}

                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setPreviewDrill(null)}
                    className="flex-1 bg-[#1A2B42] text-[#D1D9E0] text-sm font-medium rounded-lg py-2 hover:bg-[#253756] transition-colors"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      onToggleDrill(previewDrill);
                      setPreviewDrill(null);
                    }}
                    className={`flex-1 text-sm font-semibold rounded-lg py-2 btn-press transition-colors ${
                      selectedSet.has(previewDrill.id)
                        ? 'bg-red-500/20 text-red-400 hover:bg-red-500/30'
                        : 'bg-[#1A6B8A] text-white hover:bg-[#0F4A63]'
                    }`}
                  >
                    {selectedSet.has(previewDrill.id) ? 'Remove from Lesson' : 'Attach to Lesson'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
